import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  Building2,
  Users,
  TrendingUp,
  MessageSquare,
  Calendar,
  MapPin,
  Sparkles,
  ChevronRight,
  Filter,
} from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";

const ROLE_COLOR = {
  "General Contractor": "emerald",
  "Project Manager": "indigo",
  "Pre-Construction": "amber",
  "Construction Team": "violet",
  "Construction Manager": "sky",
  "Engineer": "rose",
  "Architect": "fuchsia",
  "Project Coordinator": "teal",
  "Inspector": "blue",
  "Other": "slate",
  "Unknown": "slate",
};

function fmt(n) {
  if (n == null) return "—";
  return n.toLocaleString();
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso.slice(0, 10); }
}

function Sparkline({ data }) {
  if (!data || data.length === 0) {
    return <div className="text-xs text-slate-400">No data yet — start a few chats to populate.</div>;
  }
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-24" data-testid="cities-sparkline">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
          <div
            className="w-full bg-gradient-to-t from-indigo-500 to-indigo-300 rounded-t"
            style={{ height: `${Math.max(8, (d.count / max) * 96)}px` }}
          />
          <div className="text-[9px] font-mono text-slate-400 truncate w-full text-center">{d.date.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

export default function CitiesAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jurisdiction, setJurisdiction] = useState("");
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (jurisdiction) qs.set("jurisdiction", jurisdiction);
    qs.set("days", String(days));
    api.get(`/chat/analytics/cities?${qs.toString()}`)
      .then((r) => {
        setData(r.data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "Failed to load analytics");
        setLoading(false);
      });
  }, [jurisdiction, days]);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin" && !user.city_partner) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-md mx-auto mt-24 p-8 text-center bg-white border border-slate-200 rounded-2xl" data-testid="cities-no-access">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <h2 className="font-outfit text-2xl font-bold text-slate-900">Cities Insights — restricted</h2>
          <p className="mt-2 text-slate-600 text-sm">
            This dashboard is reserved for Coast To Coast Permitting administrators and verified city partners.
            If you represent a U.S. city, county, or metro authority and want to see what permit professionals
            are asking about your jurisdiction, reach out at{" "}
            <a href="mailto:cities@ctcpermits.com" className="underline text-slate-900">cities@ctcpermits.com</a>.
          </p>
          <Button asChild className="mt-5 bg-slate-900 hover:bg-slate-800 text-white">
            <Link to="/hot-markets">Back to dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  // City partners are server-side scoped to their granted jurisdiction.
  // Lock the filter input for them so the UI matches what the API will return.
  const partnerLocked = user && !user.role.includes("admin") && user.city_partner;
  const lockedJurisdiction = partnerLocked ? user.city_partner_jurisdiction : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="cities-analytics-page">
      <Header />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 mb-4">
              <Sparkles className="w-3 h-3 text-indigo-600" />
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-indigo-700">Cities Insights &middot; Beta</span>
            </div>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
              What construction pros are <br />
              <span className="text-slate-500">asking about your jurisdiction.</span>
            </h1>
            <p className="mt-3 text-slate-600 max-w-2xl">
              Anonymized aggregate from GCs, PMs, Engineers, Architects, and Inspectors across the network. Tag-filtered, role-segmented, never personally identifiable.
            </p>
          </div>
          <Badge variant="outline" className="font-mono text-[10px]" data-testid="cities-window-badge">
            <Calendar className="w-3 h-3 mr-1.5" /> Last {days} days
          </Badge>
        </div>

        {/* Filters */}
        <div className="flex items-end gap-3 flex-wrap mb-8 p-4 bg-white border border-slate-200 rounded-xl">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">
              Jurisdiction filter
              {partnerLocked && <span className="ml-1.5 normal-case text-emerald-700">(scoped to your jurisdiction)</span>}
            </div>
            <input
              type="text"
              value={partnerLocked ? lockedJurisdiction : jurisdiction}
              onChange={(e) => !partnerLocked && setJurisdiction(e.target.value)}
              readOnly={partnerLocked}
              placeholder="e.g. Maricopa County AZ"
              data-testid="cities-juris-filter"
              className={`w-72 px-3 py-2 border rounded-md text-sm focus:outline-none ${
                partnerLocked
                  ? "border-emerald-300 bg-emerald-50 text-emerald-900 font-medium"
                  : "border-slate-300 focus:border-slate-500"
              }`}
            />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">Window</div>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="cities-window-select"
              className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:border-slate-500"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
          </div>
          <Button onClick={load} variant="outline" data-testid="cities-apply" className="h-10">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Apply
          </Button>
        </div>

        {loading && <div className="text-slate-500" data-testid="cities-loading">Loading analytics…</div>}
        {error && <div className="text-red-600" data-testid="cities-error">{error}</div>}

        {!loading && !error && data && (
          <>
            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="cities-kpis">
              <Kpi icon={<MessageSquare className="w-4 h-4" />} label="Total questions" value={fmt(data.total_questions)} color="indigo" />
              <Kpi icon={<MapPin className="w-4 h-4" />} label="Unique jurisdictions" value={fmt(data.unique_jurisdictions)} color="emerald" />
              <Kpi icon={<Users className="w-4 h-4" />} label="Roles active" value={fmt(data.by_role.length)} color="amber" />
              <Kpi icon={<TrendingUp className="w-4 h-4" />} label="Days of data" value={`${data.window_days}d`} color="rose" />
            </div>

            {/* Volume over time + by role */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6" data-testid="cities-volume-card">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Question volume</div>
                    <div className="font-outfit text-xl font-bold text-slate-900 mt-1">Daily inbound questions</div>
                  </div>
                  <BarChart3 className="w-5 h-5 text-slate-400" />
                </div>
                <Sparkline data={data.daily} />
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="cities-roles-card">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Asked by</div>
                    <div className="font-outfit text-xl font-bold text-slate-900 mt-1">Role breakdown</div>
                  </div>
                  <Users className="w-5 h-5 text-slate-400" />
                </div>
                {data.by_role.length === 0 ? (
                  <div className="text-xs text-slate-400">No questions yet.</div>
                ) : (
                  <ul className="space-y-2.5">
                    {data.by_role.map((r) => {
                      const max = data.by_role[0]?.count || 1;
                      const pct = Math.round((r.count / max) * 100);
                      const color = ROLE_COLOR[r.role] || "slate";
                      return (
                        <li key={r.role} data-testid={`role-row-${r.role.replace(/\s+/g, "-")}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-700 font-medium">{r.role}</span>
                            <span className="font-mono text-slate-500">{r.count}</span>
                          </div>
                          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full bg-${color}-500 rounded-full`} style={{ width: `${pct}%`, background: `var(--tw-bg-${color}-500, #6366f1)` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* Top jurisdictions */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-10" data-testid="cities-juris-card">
              <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Where activity is happening</div>
                  <div className="font-outfit text-xl font-bold text-slate-900 mt-1">Top jurisdictions (by question volume)</div>
                </div>
                <Building2 className="w-5 h-5 text-slate-400" />
              </div>
              {data.by_jurisdiction.length === 0 ? (
                <div className="p-6 text-sm text-slate-500">No jurisdiction-tagged questions yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-[0.12em] text-slate-500">
                    <tr>
                      <th className="text-left px-6 py-3">Jurisdiction</th>
                      <th className="text-right px-6 py-3">Questions</th>
                      <th className="text-right px-6 py-3">Unique roles</th>
                      <th className="text-right px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_jurisdiction.map((j) => (
                      <tr key={j.jurisdiction} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`juris-row`}>
                        <td className="px-6 py-3 font-medium text-slate-900">{j.jurisdiction}</td>
                        <td className="px-6 py-3 text-right font-mono">{fmt(j.count)}</td>
                        <td className="px-6 py-3 text-right">
                          <Badge variant="outline" className="font-mono text-[10px]">{j.unique_roles}</Badge>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => setJurisdiction(j.jurisdiction)}
                            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center"
                          >
                            Drill down <ChevronRight className="w-3 h-3 ml-0.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Recent questions */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="cities-recent-card">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Field intelligence</div>
                  <div className="font-outfit text-xl font-bold text-slate-900 mt-1">Recent questions</div>
                  <p className="text-xs text-slate-500 mt-1">Anonymized · role + jurisdiction tagged</p>
                </div>
              </div>
              {data.recent_questions.length === 0 ? (
                <div className="text-sm text-slate-500">No questions in this window yet — chat with the assistant a few times to populate.</div>
              ) : (
                <ul className="space-y-3">
                  {data.recent_questions.map((q, i) => {
                    const color = ROLE_COLOR[q.role] || "slate";
                    return (
                      <li key={i} className="border-l-2 pl-4 py-1" style={{ borderColor: `var(--tw-border-${color}-400, #6366f1)` }} data-testid={`recent-question-${i}`}>
                        <div className="text-sm text-slate-800 leading-relaxed">{q.question}</div>
                        <div className="mt-1 flex items-center gap-3 text-[11px] font-mono text-slate-500">
                          <span className="font-semibold">{q.role}</span>
                          {q.jurisdiction && <><span>·</span><span>{q.jurisdiction}</span></>}
                          <span>·</span>
                          <span>{fmtDate(q.created_at)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Sales pitch footer */}
            <section className="mt-12 bg-slate-900 text-white rounded-2xl p-8 md:p-12" data-testid="cities-sales-cta">
              <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl">
                Pitching this to your city?
              </h2>
              <p className="mt-3 text-slate-300 max-w-xl">
                We&apos;ll build a custom report on what professionals are actually asking about your jurisdiction —
                broken down by role, top friction points, and how your turnaround times compare to neighboring AHJs.
              </p>
              <div className="mt-6 flex gap-3 flex-wrap">
                <Button asChild className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-12 px-7" data-testid="cities-contact-cta">
                  <a href="mailto:cities@ctcpermits.com?subject=Cities%20Insights%20demo%20request">Request a custom report</a>
                </Button>
                <Button asChild variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-7" data-testid="cities-hot-markets-cta">
                  <Link to="/hot-markets">See hot markets</Link>
                </Button>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Kpi({ icon, label, value, color }) {
  const bg = { indigo: "bg-indigo-50 text-indigo-700", emerald: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", rose: "bg-rose-50 text-rose-700" }[color] || "bg-slate-100 text-slate-700";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${bg} mb-3`}>{icon}</div>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="font-outfit text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
